import { useState } from 'react';
import { Modal, Button, Input, Textarea } from '../common';
import { supabase } from '../../services/supabaseClient';

/**
 * Add Restaurant Modal
 * Allows admin and marketers to add new restaurants
 * Auto-assigns to the current user (marketer) with a note
 */
export function AddRestaurantModal({ isOpen, onClose, showToast, currentUser, onRestaurantAdded }) {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    area: '',
    mapsUrl: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const updateField = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    // Validate required fields
    if (!formData.name || !formData.phone) {
      showToast('❌ يرجى إدخال اسم المطعم ورقم الهاتف', 'error');
      return;
    }

    setSubmitting(true);
    try {
      // Add note showing who added the restaurant
      const addedByNote = currentUser.role === 'marketer' 
        ? `📝 أضافته المنسّقة: ${currentUser.name}\n${formData.notes}`
        : formData.notes;

      const newRestaurant = {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        area: formData.area?.trim() || '',
        maps_url: formData.mapsUrl?.trim() || '',
        notes: addedByNote.trim(),
        status: 'new',
        // Auto-assign to marketer if role is marketer
        assigned_to: currentUser.role === 'marketer' ? currentUser.id : null,
        // Mark as claimed if marketer is adding
        claimed_by: currentUser.role === 'marketer' ? currentUser.id : null,
        claimed_at: currentUser.role === 'marketer' ? new Date().toISOString() : null,
      };

      const { data, error } = await supabase
        .from('restaurants')
        .insert(newRestaurant)
        .select()
        .single();

      if (error) throw error;

      showToast(
        currentUser.role === 'marketer'
          ? `✅ تم إضافة "${formData.name}" وتعيينه لك بنجاح`
          : `✅ تم إضافة "${formData.name}" بنجاح`
      );

      // Reset form
      setFormData({
        name: '',
        phone: '',
        area: '',
        mapsUrl: '',
        notes: '',
      });

      // Notify parent component
      onRestaurantAdded?.();
      onClose();
    } catch (err) {
      console.error('Add restaurant error:', err);
      showToast('❌ فشل في إضافة المطعم: ' + err.message, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal 
      title="🍽️ إضافة مطعم جديد" 
      isOpen={isOpen} 
      onClose={onClose}
      size="md"
    >
      <div style={{ direction: 'rtl' }}>
        {/* Info Banner for Marketers */}
        {currentUser?.role === 'marketer' && (
          <div style={{
            marginBottom: 20,
            padding: 12,
            background: 'rgba(16, 185, 129, 0.1)',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}>
            <span style={{ fontSize: 20 }}>ℹ️</span>
            <div style={{ color: '#10b981', fontSize: 13 }}>
              سيتم تعيين المطعم لك تلقائياً ويظهر في "جهات اتصالي"
            </div>
          </div>
        )}

        {/* Restaurant Name */}
        <Input
          label="اسم المطعم *"
          value={formData.name}
          onChange={e => updateField('name', e.target.value)}
          placeholder="مطعم الشامي"
          autoFocus
        />

        {/* Phone */}
        <Input
          label="رقم الهاتف *"
          type="tel"
          value={formData.phone}
          onChange={e => updateField('phone', e.target.value)}
          placeholder="+971 50 123 4567"
        />

        {/* Area */}
        <Input
          label="المنطقة"
          value={formData.area}
          onChange={e => updateField('area', e.target.value)}
          placeholder="دبي مارينا"
        />

        {/* Maps URL */}
        <Input
          label="رابط الخريطة (Google Maps)"
          value={formData.mapsUrl}
          onChange={e => updateField('mapsUrl', e.target.value)}
          placeholder="https://maps.google.com/..."
        />

        {/* Notes */}
        <Textarea
          label="ملاحظات"
          value={formData.notes}
          onChange={e => updateField('notes', e.target.value)}
          placeholder="أي ملاحظات إضافية عن المطعم..."
          rows={3}
        />

        {/* Submit Button */}
        <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
          <Button 
            onClick={handleSubmit} 
            disabled={submitting}
            variant="primary"
            style={{ flex: 1 }}
          >
            {submitting ? '⏳ جاري الإضافة...' : '✅ إضافة المطعم'}
          </Button>
          <Button 
            onClick={onClose} 
            variant="secondary"
            disabled={submitting}
          >
            إلغاء
          </Button>
        </div>
      </div>
    </Modal>
  );
}
